import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Card,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useContext,useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { NewFormContext } from '../../context/NewFormContext';
import AttToken from '../../helpers/attToken';
import NovaNotificacao from '../../service/NovaNotificacao';
import { ChecklistType } from '../../types/checklist/types';
import { returnType } from '../../types/helpers/types';
import GenericAutoComplete from '../GenericAutoComplete';

const notificacaoService = new NovaNotificacao();
type propsNotifis = {
  obj: returnType,
  expanded: string | boolean,
  handleChange: Function,
};

const NotifisByType = ({ obj, expanded, handleChange }: propsNotifis) => {
  const { t } = useTranslation('translation');
  const { modelo, setModelo } = useContext(NewFormContext);

  function handleNotificaEm(eve: string, id: string) {
    setModelo((prev: ChecklistType) => {
      const updatedNotifiConfig = prev?.notifiConfig?.map((ele) =>
        ele.id === id
          ? { ...ele, notifiEm: eve }
          : ele
      );
      return {
        ...prev,
        notifiConfig: updatedNotifiConfig,
      };
    });
  }

  const handleServices = (values: string[]) => {
    setModelo((prev: ChecklistType) => {
      const updatedNotifiConfig = values.map((ele) => {
        const exists = prev?.notifiConfig?.some((elem) => elem.id === ele);
        if (exists) {
          return prev?.notifiConfig?.find((elem) => elem.id === ele)!;
        } else {
          return { id: ele, notifiEm: 'sempre' };
        }
      });
      return {
        ...prev,
        notifiConfig: updatedNotifiConfig,
      };
    });
  };

  return (
    <Card sx={{ width: '100%', padding: 2 }}>
      <Stack direction='row' width='100%' justifyContent='space-between' alignItems='center'>
        <Typography color='textDisabled' fontSize='small'>
          Notificações do tipo - <b>{obj.nome}</b>
        </Typography>
        <Tooltip title='Exibir/Ocultar'>
          <IconButton color="info" onClick={(isExpanded) => {
            if (expanded === obj.tipoId) {
              handleChange(!isExpanded, '');
            } else
              handleChange(isExpanded, obj.tipoId);
          }}>
            {expanded === obj.tipoId
              ? <VisibilityOffIcon />
              : <RemoveRedEyeIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {expanded === obj.tipoId && (
        <Stack direction='column' width='100%' spacing={1.5}>
          {modelo?.notifiConfig
            ?.filter((ele) => obj.notifis
              ?.some((notifi) => notifi.id === ele.id))
            ?.map((elem) => {
              const notifiNome = obj.notifis.find((notifi) => notifi.id === elem.id)?.nome || 'Nome não encontrado';
              return (
                <Stack
                  sx={{ backgroundColor: '#F3F3F4' }}
                  direction="row"
                  padding={1}
                  justifyContent='space-between'
                  alignItems="center"
                  width={'100%'}
                  key={elem.id}
                >
                  <Typography fontSize='small'>{notifiNome}</Typography>
                  <FormControl sx={{ width: 150 }}>
                    <InputLabel id="demo-simple-select-label">Notificar em:</InputLabel>
                    <Select
                      label={t('formDetails.configChamados.Obrigatorio')}
                      onChange={(eve) => handleNotificaEm(eve.target.value, elem.id)}
                      value={modelo?.notifiConfig?.find((ele) => ele.id === elem.id)?.notifiEm || 'sempre'}
                    >
                      <MenuItem value="sempre">{t('checklists.formModal.always')}</MenuItem>
                      <MenuItem value="naoConformidade">{t('checklists.formModal.inNonConformity')}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              );
            })}
          <Divider />
          <GenericAutoComplete
            options={obj.notifis?.map((e) => ({ label: e.nome, value: e.id, key: e })) ?? []}
            selectedValues={modelo?.notifiConfig?.map((ele) => ele.id) || []}
            onChange={(values) => handleServices(values)}
            label={t('checklists.formModal.selectServiceType')}
          />
        </Stack>
      )}
    </Card>
  );
};

export default function Notificacoes() {
  const { modelo, setModelo } = useContext(NewFormContext);
  const [modeloNotificacoes, setModeloNotificacoes] = useState<returnType[]>([]);
  const [expanded, setExpanded] = useState<string | boolean>(false);
  const { t } = useTranslation('translation');


  function handleChange(isExpanded: boolean, panel: string) {
    setExpanded(isExpanded ? panel : false);
  };

  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ['GET_NOTIFICACOES'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const result: returnType[] = await notificacaoService.getAllByTypes(body);
          if (!result) throw new Error('No data returned from notificacaoService');
          return result;
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  if (isError) {
    toast.error(t('custom.erro_ao_buscar_notifica_es'), { toastId: 'custom.erro_ao_buscar_notifica_es' });
  }

  useEffect(() => {
    if (isSuccess && data) {
      setModeloNotificacoes(data?.filter((ele) => modelo?.notifiConfig?.some(config => ele.notifis.some(notifi => notifi.id === config.id))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, setModelo, setModeloNotificacoes]);

  function handleServiceTypes(valor: string[]) {
    const itens = data?.filter((ele) => valor.includes(ele.tipoId));
    setModeloNotificacoes(itens as returnType[]);
  }

  return (
    <Stack width='100%' direction="column" justifyContent="center" alignItems="center" spacing={2}>
      <Typography color='textDisabled' variant='h5' textAlign='center' fontWeight={600}>
        Configurações de notificação
      </Typography>
      <Typography variant='h6'>{t('checklists.formModal.serviceTypesNotifiedByForm')}</Typography>
      <GenericAutoComplete
        loading={isLoading || isFetching}
        options={data?.map((e) => ({ label: e.nome, value: e.tipoId, key: e })) ?? []}
        selectedValues={modeloNotificacoes?.map((ele) => ele.tipoId) || []}
        onChange={(values) => handleServiceTypes(values)}
        label={t('checklists.formModal.selectServiceType')}
      />
      {modeloNotificacoes?.map(obj => (
        <NotifisByType
          key={obj.tipoId}
          expanded={expanded}
          handleChange={handleChange}
          obj={obj}
        />
      ))}
    </Stack>
  );
}
